import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ClipboardList, BadgeCheck, MailCheck } from "lucide-react";
import { Eyebrow, Reveal } from "./Reveal";

const STEPS = [
    {
        n: "1", icon: ClipboardList, title: "Request",
        body: "A manager spots an empty desk and submits an allocation request for a new joiner through IDeaSpace.",
    },
    {
        n: "2", icon: BadgeCheck, title: "Approve & Set Up",
        body: "Admin reviews and approves. IT gets an automatic notification to set up the workstation.",
    },
    {
        n: "3", icon: MailCheck, title: "Done",
        body: "The desk is marked active. Everyone involved gets a confirmation email. Done.",
    },
];

export function HowItWorks() {
    const reduce = useReducedMotion();
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
    const yBg = useTransform(scrollYProgress, [0, 1], [reduce ? "0%" : "-10%", reduce ? "0%" : "10%"]);

    return (
        <section ref={ref} data-testid="how-it-works-section" className="relative py-20 sm:py-28 overflow-hidden">
            <motion.div style={{ y: yBg }} className="dot-grid absolute -top-[15%] left-0 right-0 h-[130%] opacity-60 pointer-events-none" />

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <Reveal className="max-w-2xl mb-12 sm:mb-16">
                    <Eyebrow>05 · Process</Eyebrow>
                    <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[var(--ink)]">
                        How a Desk Allocation Works
                    </h2>
                </Reveal>

                <div className="relative grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8">
                    <div className="hidden md:block absolute top-8 left-[16%] right-[16%] border-t border-dashed border-[var(--line-strong)]" aria-hidden="true" />
                    {STEPS.map((s, i) => {
                        const Icon = s.icon;
                        return (
                            <Reveal key={s.n} delay={i * 0.12}>
                                <div data-testid={`how-it-works-step-${s.n}`} className="relative flex md:flex-col md:items-start gap-5">
                                    <div className="relative shrink-0">
                                        <div className="w-16 h-16 rounded-2xl bg-[var(--paper)] border border-[var(--line)] shadow-[0_6px_18px_-6px_rgba(15,23,42,0.1)] grid place-items-center text-[var(--accent)]">
                                            <Icon size={26} strokeWidth={1.7} />
                                        </div>
                                        <span className="absolute -top-2.5 -right-2.5 w-7 h-7 rounded-full bg-[var(--accent)] text-[#0A0908] mono text-xs font-semibold grid place-items-center">
                                            {s.n}
                                        </span>
                                    </div>
                                    <div>
                                        <h3 className="font-display font-bold text-xl tracking-tight text-[var(--ink)]">{s.title}</h3>
                                        <p className="mt-2 text-sm sm:text-base leading-relaxed text-[var(--ink-2)] max-w-sm">{s.body}</p>
                                    </div>
                                </div>
                            </Reveal>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
